/**
 * Channel Key Names. Union type of names used.
 */
export type ChannelKeyName =
    | "Direct"
    | "Organic Search"
    | "Paid Search"
    | "Referrals"
    | "Display Ads"
    | "Organic Social"
    | "Paid Social"
    | "Gen AI"
    | "Affiliates"
    | "Email"
    | "Internal Referrals";

/**
 * Descriptive channel names. Should be unified.
 */
export type ChannelDescriptions =
    | "Search - Organic"
    | "Search - Paid"
    | "Social - Organic"
    | "Social - Paid"
    | "Paid Referrals";

/**
 * Canonical channel names for MMX6 channels, no obsoletes. PascalCased (later could be camelCased), used as keys in BE contracts.
 */
export type ChannelMMX6Canonical =
    | "Direct"
    | "OrganicSearch"
    | "PaidSearch"
    | "Referrals"
    | "PaidReferrals"
    | "OrganicSocial"
    | "PaidSocial"
    | "Mail"
    | "GenAi"
    | "Affiliates"
    | "InternalReferrals";

/**
 * Canonical channel names, holds historical names as well. PascalCased (later could be camelCased), used as keys in BE contracts.
 */
export type ChannelCanonical = ChannelMMX6Canonical | "Search" | "DisplayAds" | "Email" | "Social";

/**
 * Descriptor for a single MMX marketing channel.
 * Ties together every representation of a channel so they don't need to be
 * re-declared across the segments/mmx feature.
 */
export interface IMmxChannel {
    /** PascalCase name used as a key in BE contracts. */
    canonical: ChannelMMX6Canonical;
    /** Human/BE data key, also used to look up chart colors. */
    key: ChannelKeyName;
    /** Default display label. */
    display: string;
    /** Optional i18n key for a channel-level tooltip. */
    tooltip?: string;
}

/**
 * Single source of truth for the MMX Segments channels.
 * Base order matches the channels overview bar chart. All other channel
 * structures (`CHANNEL_KEY_NAMES`, `ChannelsObj`, `SegmentsMmxChannelsMapping`, …)
 * are derived from this list.
 */
export const MMX_CHANNELS = [
    { canonical: "Direct", key: "Direct", display: "Direct" },
    { canonical: "OrganicSearch", key: "Organic Search", display: "Organic search" },
    { canonical: "PaidSearch", key: "Paid Search", display: "Paid search" },
    { canonical: "Referrals", key: "Referrals", display: "External Referrals" },
    { canonical: "PaidReferrals", key: "Display Ads", display: "Display Ads" },
    { canonical: "OrganicSocial", key: "Organic Social", display: "Organic social" },
    { canonical: "PaidSocial", key: "Paid Social", display: "Paid social" },
    { canonical: "GenAi", key: "Gen AI", display: "Gen AI" },
    { canonical: "Affiliates", key: "Affiliates", display: "Affiliates" },
    { canonical: "Mail", key: "Email", display: "Email" },
    {
        canonical: "InternalReferrals",
        key: "Internal Referrals",
        display: "Internal Referrals",
        tooltip: "segments.analysis.mmx.single.chart.label.tooltip.internalReferrals",
    },
] as const satisfies readonly IMmxChannel[];

/**
 * Array of all channel key names used in MMX Segments (derived from {@link MMX_CHANNELS}).
 */
export const CHANNEL_KEY_NAMES = MMX_CHANNELS.map(({ key }) => key) satisfies readonly ChannelKeyName[];

/**
 * Identity helper that only type-checks when `order` lists **every**
 * {@link ChannelMMX6Canonical}. Feature-specific column/legend orders are still
 * authored by hand (they differ per view), but this makes a forgotten channel a
 * compile error instead of a silent omission. Pass the array `as const`.
 */
export const assertExhaustiveCanonicalOrder = <T extends readonly ChannelMMX6Canonical[]>(
    order: T & ([ChannelMMX6Canonical] extends [T[number]] ? unknown : never),
): T => order;

/**
 * Metric Key Names. Union type of metrics used.
 */
export type MetricKeyNames = "Visits" | "PageViews" | "PagesPerVisit" | "Duration" | "BounceRate";

/**
 * Segment ID type.
 * Currently same as `string`
 */
export type SegmentId = string;
